import React from 'react';
import { Admin, Resource, CustomRoutes } from 'react-admin';
import { Route } from 'react-router-dom';
import authProvider from './authProvider';
import {
    Category as CategoryIcon,
    Quiz as QuestionIcon,
    Assessment as ResultIcon,
    School as CourseIcon,
    Article as TemplateIcon,
    History as LogIcon,
    People as UsersIcon,
    AutoFixHigh as WizardIcon,
    Book as BookIcon,
    Security as SecurityIcon,
    ContactMail as RequestIcon
} from '@mui/icons-material';

import AdminLayout from './AdminLayout';
import AdminDashboard from './AdminDashboard';
import AdminLoginPage from './AdminLoginPage';
import dataProvider from './dataProvider';
import { UserList, UserEdit, UserCreate } from './UserResources';
import { UserLogList } from './UserLogResources';
import { CourseList, CourseEdit, CourseCreate } from './CourseResources';
import { CourseQuestionList, CourseQuestionEdit, CourseQuestionCreate } from './CourseQuestionResources';
import { CourseResultList, CourseResultEdit } from './CourseResultResources';
import { PromptTemplateList, PromptTemplateEdit, PromptTemplateCreate, PromptTemplateShow } from './PromptTemplateResources';
import { CourseWizardList, CourseWizardCreate, CourseWizardEdit } from './CourseWizardResources';
import { CourseCategoryList, CourseCategoryEdit, CourseCategoryCreate } from './CategoryResources';
import { CourseTextbookList } from './CourseTextbookResources';
import { ApprovedTrainingUserList, ApprovedTrainingUserCreate } from './ApprovedTrainingUserResources';
import { TrainingAccessRequestList, TrainingAccessRequestDetail } from './TrainingAccessRequestResources';
import CourseGenerator from './CourseGenerator';
import CourseMaterialWizard from './CourseMaterialWizard';
import ExamWizard from './ExamWizard';
import TextbookEditor from './TextbookEditor';
import { MediaFileList, MediaFileCreate, MediaFileEdit, MediaIcon } from './MediaFileResources';
import { CourseReviewNoteList, CourseReviewNoteCreate, CourseReviewNoteEdit, CourseReviewNoteIcon } from './CourseReviewNoteResources';

// Use custom dataProvider pointing to /api/admin with RBAC headers

// Use basename to tell React Admin that it's mounted under /admin path
// This helps it understand the URL structure

const AppAdmin = () => (
    <Admin
        authProvider={authProvider}
        dataProvider={dataProvider}
        dashboard={AdminDashboard}
        layout={AdminLayout}
        loginPage={AdminLoginPage}>
        <Resource
            name="users"
            list={UserList}
            edit={UserEdit}
            create={UserCreate}
            icon={UsersIcon}
            options={{ label: 'Users' }}
        />
        <Resource
            name="user_logs"
            list={UserLogList}
            icon={LogIcon}
            options={{ label: 'User Logs' }}
        />
        <Resource
            name="courses"
            list={CourseList}
            edit={CourseEdit}
            create={CourseCreate}
            icon={CourseIcon}
            options={{ label: 'Courses' }}
        />
        <Resource
            name="course_questions"
            list={CourseQuestionList}
            edit={CourseQuestionEdit}
            create={CourseQuestionCreate}
            icon={QuestionIcon}
            options={{ label: 'Course Questions' }}
        />
        <Resource
            name="course_results"
            list={CourseResultList}
            edit={CourseResultEdit}
            icon={ResultIcon}
            options={{ label: 'Course Results' }}
        />
        <Resource
            name="prompt_templates"
            list={PromptTemplateList}
            edit={PromptTemplateEdit}
            create={PromptTemplateCreate}
            show={PromptTemplateShow}
            icon={TemplateIcon}
            options={{ label: 'Prompt Templates' }}
        />
        <Resource
            name="course_wizard"
            list={CourseWizardList}
            edit={CourseWizardEdit}
            create={CourseWizardCreate}
            icon={WizardIcon}
            options={{ label: 'Course Wizard' }}
        />
        <Resource
            name="course_categories"
            list={CourseCategoryList}
            edit={CourseCategoryEdit}
            create={CourseCategoryCreate}
            icon={CategoryIcon}
            options={{ label: 'Course Categories' }}
        />
        <Resource
            name="course_material_content"
            list={CourseTextbookList}
            icon={BookIcon}
            options={{ label: 'Course Textbooks' }}
        />
        <Resource
            name="approved-training-users"
            list={ApprovedTrainingUserList}
            create={ApprovedTrainingUserCreate}
            icon={SecurityIcon}
            options={{ label: 'Approved Training Users' }}
        />
        <Resource
            name="training-access-requests"
            list={TrainingAccessRequestList}
            icon={RequestIcon}
            options={{ label: 'Training Access Requests' }}
        />
        <Resource
            name="media_files"
            list={MediaFileList}
            create={MediaFileCreate}
            edit={MediaFileEdit}
            icon={MediaIcon}
            options={{ label: 'Media Manager' }}
        />
        <Resource
            name="course_review_notes"
            list={CourseReviewNoteList}
            create={CourseReviewNoteCreate}
            edit={CourseReviewNoteEdit}
            icon={CourseReviewNoteIcon}
            options={{ label: 'Course Review Notes' }}
        />
        <CustomRoutes>
            <Route path="/course-generator" element={<CourseGenerator />} />
            <Route path="/course-material-wizard" element={<CourseMaterialWizard />} />
            <Route path="/exam-wizard" element={<ExamWizard />} />
            <Route path="/training-access-requests/:id" element={<TrainingAccessRequestDetail />} />
            <Route path="/textbook-editor/:id" element={<TextbookEditor />} />
        </CustomRoutes>
    </Admin>
);

export default AppAdmin;
